import { useCallback, useRef, useState, type ReactNode } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogOverlay, DialogPortal } from '@/ui/components/common/Dialog'
import { classNames } from '@/ui/utils/main'

/**
 * Opening is slower than closing: growing out of the thumbnail is the effect
 * worth watching, while getting back to the article should feel immediate.
 */
const OPEN_DURATION_MS = 300
const CLOSE_DURATION_MS = 200
/** Decelerating on the way out, accelerating on the way back in. */
const OPEN_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)'
const CLOSE_EASING = 'cubic-bezier(0.4, 0, 1, 1)'

/**
 * The Web Animations API is missing under jsdom and unwelcome when the reader
 * has asked for less motion. Both cases fall back to showing/hiding the
 * overlay outright, so the lightbox stays fully usable without animation.
 */
function canAnimate(element: Element | null): element is Element {
  if (element === null || typeof element.animate !== 'function') {
    return false
  }
  if (typeof window.matchMedia !== 'function') {
    return true
  }

  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * The FLIP transform that maps the enlarged image onto `origin`.
 *
 * Both rectangles are measured on screen, so the delta is expressed from the
 * two centres and the animation needs no `transform-origin` of its own. `scale`
 * is per-axis rather than uniform so the start of the flight matches the
 * thumbnail box exactly; every thumbnail the lightbox wraps preserves the
 * image's aspect ratio, so the two factors coincide in practice and nothing
 * appears squashed mid-flight.
 */
function flipTransform(origin: DOMRect, target: DOMRect): string {
  const scaleX = origin.width / target.width
  const scaleY = origin.height / target.height
  const translateX = origin.left + origin.width / 2 - (target.left + target.width / 2)
  const translateY = origin.top + origin.height / 2 - (target.top + target.height / 2)

  return `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`
}

interface ImageLightboxProps {
  /**
   * Image source for the enlarged copy. Callers must pass an already-sanitised
   * URL — for markdown-embedded images this is the `src` react-markdown hands
   * over, which its default `urlTransform` has already neutralised.
   */
  src: string
  /** Alternative text, reused for the enlarged copy and the accessible label. */
  alt: string
  /** Extra classes for the trigger button (layout/spacing of the thumbnail). */
  className?: string
  /** The thumbnail to make clickable — rendered inside the trigger button. */
  children: ReactNode
}

/**
 * Click-to-enlarge wrapper for an image.
 *
 * The thumbnail stays the caller's concern (it is passed as `children`, so the
 * caller keeps full control of its sizing); this component only adds the
 * clickable, keyboard-reachable trigger and the overlay that shows the same
 * image much bigger.
 *
 * Built on the Radix dialog primitive rather than on `DialogContent`, which
 * hardcodes card chrome, a constrained max-width and an English close label —
 * none of which suit a full-bleed image overlay. Composing
 * `DialogPortal` + `DialogOverlay` + `DialogPrimitive.Content` still gets the
 * focus trap, Escape-to-close, portalling and scroll lock for free.
 *
 * The content box is sized to the image (not `inset-0`) so the backdrop is
 * genuinely *outside* it: Radix's own outside-pointer-down handling then makes
 * a backdrop click dismiss the overlay, with no ad-hoc click handler.
 *
 * The enlarged copy is deliberately unconstrained by whatever the thumbnail
 * was given — an editor `=WxH` size marker, or an `object-cover` crop in a
 * fixed box: it renders whole, at its natural size and aspect ratio, capped
 * only by the viewport.
 *
 * ## Why the animation is hand-driven
 *
 * The overlay grows out of the thumbnail the reader clicked (a FLIP: measure
 * the thumbnail, start the centred image transformed onto it, animate the
 * transform away), which no utility class can express — the start rectangle is
 * only known at click time. Two consequences for anyone editing this:
 *
 * - The content box is centred with `inset-0` + `m-auto`, *not* with
 *   `-translate-x-1/2 -translate-y-1/2`, so `transform` belongs entirely to the
 *   animation. Centring via `transform` is what made the overlay appear to fly
 *   in from the bottom-right corner: `tailwindcss-animate`'s keyframes write
 *   the whole `transform` property and so dropped the centring offsets for the
 *   duration of the animation.
 * - Closing is animated *before* unmounting, so `open` is controlled here
 *   rather than left to Radix.
 */
export function ImageLightbox({ src, alt, className, children }: ImageLightboxProps) {
  const { t } = useTranslation()
  const hasAlt = alt.trim().length > 0

  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const runningRef = useRef<Animation | null>(null)
  const flightStartedRef = useRef(false)

  /**
   * The rectangle the flight starts from and returns to: the thumbnail as it
   * is drawn, which is the inner image rather than the button whenever the two
   * differ (article images bleed outside their trigger on small screens).
   * Measured on demand — never cached — so a reflow while the overlay is open
   * cannot leave the closing animation aiming at a stale position.
   */
  const readOriginRect = useCallback((): DOMRect | null => {
    const trigger = triggerRef.current
    if (trigger === null) {
      return null
    }
    const rect = (trigger.querySelector('img') ?? trigger).getBoundingClientRect()

    return rect.width > 0 && rect.height > 0 ? rect : null
  }, [])

  /**
   * Starts the flight, at most once per opening. Called from the content's ref
   * callback — i.e. the moment the node lands in the DOM, which is where a FLIP
   * has to measure. A `useLayoutEffect` keyed on `open` cannot do this job:
   * Radix's portal mounts its children in a second commit (it flips a `mounted`
   * state from a layout effect of its own), so the effect would run while the
   * ref is still empty and never run again.
   */
  const startOpenFlight = useCallback(() => {
    const content = contentRef.current
    if (flightStartedRef.current || !canAnimate(content)) {
      return
    }
    const origin = readOriginRect()
    const target = content.getBoundingClientRect()
    // A zero-sized box means the enlarged copy has no intrinsic size yet, so
    // there is nothing to fly from. The image's `onLoad` calls back here once
    // it does; appearing plainly beats flying in from the wrong place.
    if (origin === null || target.width === 0 || target.height === 0) {
      return
    }
    flightStartedRef.current = true
    runningRef.current = content.animate(
      [{ transform: flipTransform(origin, target) }, { transform: 'none' }],
      { duration: OPEN_DURATION_MS, easing: OPEN_EASING },
    )
  }, [readOriginRect])

  const attachContent = useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node
      if (node !== null) {
        startOpenFlight()
      }
    },
    [startOpenFlight],
  )

  /**
   * Every dismissal path (close button, Escape, backdrop) funnels through
   * Radix's `onOpenChange`, so reversing the flight here covers them all.
   */
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        setOpen(true)

        return
      }
      const content = contentRef.current
      flightStartedRef.current = false
      if (!canAnimate(content)) {
        setOpen(false)

        return
      }
      // Measuring while the opening flight is still mid-transform would read a
      // scaled-down rectangle, so drop that animation first: the content falls
      // back to its resting, untransformed geometry.
      runningRef.current?.cancel()
      const origin = readOriginRect()
      const target = content.getBoundingClientRect()
      if (origin === null || target.width === 0 || target.height === 0) {
        setOpen(false)

        return
      }
      // `fill: 'forwards'` holds the end state until the unmount lands, so the
      // image cannot flash back to full size between the two.
      const shrink = content.animate(
        [{ transform: 'none' }, { transform: flipTransform(origin, target) }],
        { duration: CLOSE_DURATION_MS, easing: CLOSE_EASING, fill: 'forwards' },
      )
      runningRef.current = shrink
      // The backdrop lifts alongside the image instead of after it. Radix's own
      // `data-[state=closed]` fade would only start once the state flips, which
      // is the moment this animation ends.
      overlayRef.current?.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: CLOSE_DURATION_MS,
        easing: CLOSE_EASING,
        fill: 'forwards',
      })
      shrink.addEventListener('finish', () => setOpen(false))
    },
    [readOriginRect],
  )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Trigger asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-label={hasAlt ? t('common.image_enlarge_named', { alt }) : t('common.image_enlarge')}
          className={classNames(
            'image-lightbox-trigger w-full grow group block cursor-zoom-in appearance-none border-0 bg-transparent p-0 text-left',
            // The native outline is replaced by a themed ring, so every call
            // site gets a visible keyboard focus indicator by default. Sites
            // whose thumbnail sits in an `overflow-hidden` box should pass
            // `focus-visible:ring-inset focus-visible:ring-offset-0` so the
            // ring is not clipped; sites that would rather ring the image
            // itself can switch this off with `focus-visible:ring-0`
            // (`classNames` runs tailwind-merge, so the later class wins).
            'focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            className,
          )}
        >
          {children}
        </button>
      </DialogPrimitive.Trigger>
      <DialogPortal>
        {/*
          `pointer-events-none` while closing: the backdrop is already invisible
          (the closing animation below fills its final opacity forwards), but
          Radix keeps it mounted for the duration of its own exit fade, where it
          would otherwise swallow the reader's next click.
        */}
        <DialogOverlay
          ref={overlayRef}
          className="bg-black/90 data-[state=closed]:pointer-events-none print:hidden"
        />
        {/*
          No description is rendered, so `aria-describedby` is cleared
          explicitly — otherwise Radix points it at a node that never exists.

          Centred by `inset-0` + `m-auto` on a box that still only takes the
          image's own size: the backdrop stays outside it (so a backdrop click
          dismisses), and `transform` is left free for the opening flight.
        */}
        <DialogPrimitive.Content
          ref={attachContent}
          aria-describedby={undefined}
          className="fixed inset-0 z-50 m-auto h-fit w-fit max-h-[95vh] max-w-[95vw] focus:outline-none print:hidden"
        >
          <DialogPrimitive.Title className="sr-only">
            {hasAlt ? alt : t('common.image_preview')}
          </DialogPrimitive.Title>
          <img
            src={src}
            alt={alt}
            // A cold cache means the content box had no size when the node
            // mounted, so the flight is retried here — once the image has a
            // size, there is finally something to fly from.
            onLoad={startOpenFlight}
            className="block h-auto max-h-[95vh] w-auto max-w-[95vw] rounded-md object-contain shadow-2xl"
          />
          <DialogPrimitive.Close className="absolute right-2 top-2 rounded-full bg-black/70 p-2 text-white transition-colors hover:bg-black/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
            <X className="h-5 w-5" aria-hidden="true" />
            <span className="sr-only">{t('common.image_close')}</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
